/**
 * EmptyState Component
 * Consistent empty state messaging with optional action
 * Based on LifeScribe Design System v4.0
 */

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors, typography, spacing } from '../../theme/designTokens';
import { icons, IconName } from '../../theme/icons';

export interface EmptyStateProps {
  icon?: IconName;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'contained' | 'outlined' | 'text';
  };
}

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: spacing[8],
  minHeight: '300px',
});

const IconContainer = styled(Box)(({ theme }) => ({
  width: 96,
  height: 96,
  borderRadius: '50%',
  backgroundColor: theme.palette.action.hover,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: spacing[4],
  '& .material-icons': {
    fontSize: 48,
    color: theme.palette.text.disabled,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: typography.fonts.display,
  fontSize: typography.sizes['2xl'],
  fontWeight: typography.weights.semibold,
  color: theme.palette.text.primary,
  marginBottom: spacing[2],
}));

const Description = styled(Typography)(({ theme }) => ({
  fontFamily: typography.fonts.body,
  fontSize: typography.sizes.base,
  color: theme.palette.text.secondary,
  maxWidth: '400px',
  marginBottom: spacing[6],
  lineHeight: typography.lineHeights.relaxed,
}));

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon = 'empty_box',
  title,
  description,
  action,
}) => {
  const materialIcon = icons[icon];

  return (
    <Container>
      {/* Icon */}
      <IconContainer>
        <span className="material-icons">{materialIcon}</span>
      </IconContainer>

      {/* Title */}
      <Title>{title}</Title>

      {/* Description */}
      {description && <Description>{description}</Description>}

      {/* Action Button */}
      {action && (
        <Button
          variant={action.variant || 'contained'}
          onClick={action.onClick}
          size="large"
          sx={{
            minWidth: 160,
            fontWeight: typography.weights.semibold,
          }}
        >
          {action.label}
        </Button>
      )}
    </Container>
  );
};

export default EmptyState;
